// src/pages/Settings.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Trash2, Download, AlertTriangle, DollarSign, Settings as SettingsIcon, Link2, Check } from 'lucide-react';


// ── Stable presentational sub-components — MUST live outside SettingsPage
// otherwise they get re-created on every keystroke, which unmounts/remounts
// each <input> and causes the focus-loss bug.
function Section({ title, icon: Icon, color = '#00e5a0', children }) {
  return (
    <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 24, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <Icon size={15} style={{ color }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#f0f0f5', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono', monospace" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Row({ label, sub, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: '1px solid #1a1a26' }}>
      <div>
        <div style={{ fontSize: 13, color: '#f0f0f5' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#55556a', marginTop: 2 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function RiskToggleRow({ settings, setSetting }) {
  const [mode, setMode] = React.useState(() => settings.riskMode || 'dollar');
  const balance = settings.startingBalance || 0;
  const riskVal = settings.riskPerTrade || 0;

  const dollarVal = mode === 'dollar'
    ? riskVal
    : balance > 0 ? ((riskVal / 100) * balance) : null;

  const pctVal = mode === 'percent'
    ? riskVal
    : balance > 0 ? ((riskVal / balance) * 100) : null;

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setSetting('riskMode', newMode);
    // Convert current value to new mode
    if (newMode === 'percent' && balance > 0 && riskVal > 0) {
      setSetting('riskPerTrade', parseFloat(((riskVal / balance) * 100).toFixed(2)));
    } else if (newMode === 'dollar' && balance > 0 && riskVal > 0) {
      setSetting('riskPerTrade', parseFloat(((riskVal / 100) * balance).toFixed(2)));
    }
  };

  const equiv = mode === 'dollar'
    ? (balance > 0 && riskVal > 0 ? `= ${((riskVal / balance) * 100).toFixed(2)}% of balance` : balance === 0 ? '— set balance to see %' : '')
    : (balance > 0 && riskVal > 0 ? `= $${((riskVal / 100) * balance).toFixed(2)} per trade` : balance === 0 ? '— set balance to see $' : '');

  return (
    <div style={{ padding:'10px 0', borderBottom:'1px solid #1a1a26' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <div style={{ fontSize:13, color:'#f0f0f5', marginBottom:2 }}>Default Risk Per Trade</div>
          <div style={{ fontSize:11, color:'#55556a', marginTop:2 }}>Used in position size calculator</div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>
          {/* Mode toggle */}
          <div style={{ display:'flex', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, padding:2, gap:2 }}>
            {[['dollar','$'],['percent','%']].map(([m, label]) => (
              <button key={m} onClick={() => handleModeChange(m)} style={{
                padding:'5px 14px', borderRadius:5, border:'none', fontSize:12, fontWeight:600,
                cursor:'pointer', fontFamily:"'DM Mono',monospace",
                background: mode===m ? '#16161f' : 'transparent',
                color: mode===m ? '#00e5a0' : '#55556a',
                transition:'all 0.15s',
              }}>{label}</button>
            ))}
          </div>
          {/* Input */}
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:13, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
              {mode === 'dollar' ? '$' : ''}
            </span>
            <input className="form-input" type="number" step="any" style={{ maxWidth:110 }}
              value={settings.riskPerTrade || ''}
              onChange={e => setSetting('riskPerTrade', parseFloat(e.target.value) || 0)}
            />
            {mode === 'percent' && <span style={{ fontSize:13, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>%</span>}
          </div>
          {/* Equivalent */}
          {equiv && (
            <div style={{ fontSize:11, color:'#00e5a0', fontFamily:"'DM Mono',monospace" }}>
              {equiv}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BalanceSaver({ activeAccountId, currentBalance, onSaved }) {
  const [val,    setVal]    = React.useState(String(currentBalance || 10000));
  const [saving, setSaving] = React.useState(false);
  const [saved,  setSaved]  = React.useState(false);

  React.useEffect(() => { setVal(String(currentBalance || 10000)); }, [currentBalance]);

  const save = async () => {
    if (!activeAccountId) return;
    setSaving(true);
    const { error } = await supabase
      .from('accounts')
      .update({ starting_balance: parseFloat(val) || 10000 })
      .eq('id', activeAccountId);
    setSaving(false);
    if (!error) {
      onSaved(parseFloat(val) || 10000);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  return (
    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
      <input className="form-input" type="number" value={val} onChange={e => setVal(e.target.value)}
        style={{ maxWidth:130 }} onKeyDown={e => e.key === 'Enter' && save()} />
      <button onClick={save} disabled={saving} style={{ background:'#00e5a0', color:'#0a0a0f', border:'none', borderRadius:7, padding:'7px 14px', fontSize:12, fontWeight:600, cursor:saving?'not-allowed':'pointer', fontFamily:"'IBM Plex Sans',sans-serif", opacity:saving?0.7:1 }}>
        {saving ? '...' : saved ? '✓' : 'Save'}
      </button>
    </div>
  );
}

function PublicProfileSection({ shareableLink }) {
  const { user } = useAuth();
  const [isPublic,  setIsPublic]  = React.useState(false);
  const [username,  setUsername]  = React.useState('');
  const [saving,    setSaving]    = React.useState(false);
  const [saved,     setSaved]     = React.useState(false);
  const [copied,    setCopied]    = React.useState(false);

  React.useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('is_public, username').eq('id', user.id).single()
      .then(({ data }) => {
        if (data) { setIsPublic(data.is_public || false); setUsername(data.username || ''); }
      });
  }, [user]);

  const save = async () => {
    if (!user) return;
    setSaving(true); setSaved(false);
    await supabase.from('profiles').update({ is_public: isPublic, username: username.toLowerCase().replace(/[^a-z0-9]/g,'') || null }).eq('id', user.id);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const copy = () => {
    if (shareableLink) { navigator.clipboard.writeText(shareableLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:3 }}>Public Profile</div>
          <div style={{ fontSize:12, color:'#55556a' }}>Allow others to view your performance via your shareable link</div>
        </div>
        <button onClick={() => setIsPublic(p => !p)} style={{
          width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
          background: isPublic ? '#00e5a0' : '#2a2a3e', transition:'background 0.2s', position:'relative', flexShrink:0
        }}>
          <div style={{ width:18, height:18, borderRadius:'50%', background:'#fff', position:'absolute', top:3, transition:'left 0.2s', left: isPublic ? 23 : 3 }} />
        </button>
      </div>

      {isPublic && (
        <>
          <div>
            <label style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', display:'block', marginBottom:6 }}>Username</label>
            <input value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g,''))}
              placeholder="e.g. odftrader"
              style={{ width:'100%', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:8, color:'#f0f0f5', padding:'10px 12px', fontSize:13, fontFamily:"'IBM Plex Sans',sans-serif", outline:'none', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor='#00e5a0'} onBlur={e=>e.target.style.borderColor='#1e1e2e'} />
            <div style={{ fontSize:11, color:'#55556a', marginTop:4, fontFamily:"'DM Mono',monospace" }}>odftrades.com/u/{username||'yourname'}</div>
          </div>
          {shareableLink && (
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1, background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#55556a', fontFamily:"'DM Mono',monospace", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {shareableLink}
              </div>
              <button onClick={copy} style={{
                display:'inline-flex', alignItems:'center', gap:6,
                background: copied ? 'rgba(0,229,160,0.1)' : 'transparent',
                border:`1px solid ${copied ? 'rgba(0,229,160,0.3)' : '#1e1e2e'}`,
                borderRadius:8, padding:'10px 16px', cursor:'pointer',
                color: copied ? '#00e5a0' : '#8888a8', fontSize:12,
                fontFamily:"'IBM Plex Sans',sans-serif", whiteSpace:'nowrap', transition:'all 0.2s'
              }}>
                {copied ? <><Check size={13} /> Copied</> : <><Link2 size={13} /> Copy Link</>}
              </button>
            </div>
          )}
        </>
      )}

      <button onClick={save} disabled={saving} style={{
        background:'#00e5a0', color:'#0a0a0f', border:'none', borderRadius:8,
        padding:'11px', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer',
        fontFamily:"'IBM Plex Sans',sans-serif", opacity:saving?0.7:1, alignSelf:'flex-start', minWidth:120
      }}>
        {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
      </button>
    </div>
  );
}

export default function SettingsPage({ trades, settings, setSettings, onClear, shareableLink, accounts, activeAccountId , lang = 'en'}) {
  const [wAmount,   setWAmount]   = useState('');
  const [wDate,     setWDate]     = useState(new Date().toISOString().slice(0,10));
  const [wNote,     setWNote]     = useState('');
  const [wSaved,    setWSaved]    = useState(false);

  const setSetting = (k, v) => setSettings(s => ({ ...s, [k]: v }));


  const addWithdrawal = () => {
    const amt = parseFloat(wAmount);
    if (!amt || amt <= 0) return;
    const w = { id: Date.now(), date: wDate, amount: amt, note: wNote };
    setSetting('withdrawals', [...(settings.withdrawals || []), w]);
    setWAmount(''); setWNote(''); setWSaved(true);
    setTimeout(() => setWSaved(false), 2000);
  };

  const removeWithdrawal = (id) => {
    setSetting('withdrawals', (settings.withdrawals || []).filter(w => w.id !== id));
  };

  const exportData = () => {
    const headers = ['date','pair','session','direction','entryPrice','stopLoss','takeProfit','riskAmount','pnl','result','rrAchieved','lotSize','followedRules','mistakeMade','emotionBefore','emotionAfter','notes','poi','orderFlow','analysisTF','account'];
    const rows = trades.map(t => headers.map(h => JSON.stringify(t[h] ?? '')).join(','));
    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `odftrades_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const inp = { maxWidth: 140 };

  const totalWithdrawals = (settings.withdrawals || []).reduce((s, w) => s + w.amount, 0);

  return (
    <div className="fade-in" style={{ padding: '28px 32px', maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#f0f0f5', margin: 0 }}>Settings</h1>
        <p style={{ color: '#8888a8', fontSize: 13, margin: '4px 0 0' }}>Account, risk rules, security and data.</p>
      </div>

      {/* Account */}
      <Section title="Account Setup" icon={SettingsIcon}>
        <Row label="Starting Balance ($)" sub="Your initial deposit — changes the active account">
          <BalanceSaver activeAccountId={activeAccountId} currentBalance={settings.startingBalance || 10000} onSaved={(val) => setSetting('startingBalance', val)} />
        </Row>
        <RiskToggleRow settings={settings} setSetting={setSetting} />
      </Section>

      {/* Risk Rules */}
      <Section title="Risk Rules" icon={AlertTriangle} color="#ffd166">
        <Row label="Max Trades Per Day" sub="You stop trading after this many trades">
          <input className="form-input" type="number" style={inp} value={settings.maxTradesPerDay || 3}
            onChange={e => setSetting('maxTradesPerDay', parseInt(e.target.value))} />
        </Row>
        <Row label="Max Losses Per Day" sub="Stop after this many losing trades">
          <input className="form-input" type="number" style={inp} value={settings.maxDailyLosses || 2}
            onChange={e => setSetting('maxDailyLosses', parseInt(e.target.value))} />
        </Row>
        <Row label="Daily Loss Limit ($)" sub="Max dollar loss before stopping for the day">
          <input className="form-input" type="number" style={inp} value={settings.maxDailyLoss || 300}
            onChange={e => setSetting('maxDailyLoss', parseFloat(e.target.value))} />
        </Row>
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(255,209,102,0.06)', border: '1px solid rgba(255,209,102,0.15)', borderRadius: 8, fontSize: 12, color: '#55556a' }}>
          These rules are enforced in the Trading Command Center. Violations are flagged automatically.
        </div>
      </Section>

      {/* Withdrawals */}
      <Section title="Withdrawal Tracker" icon={DollarSign} color="#4cc9f0">
        <div style={{ fontSize: 12, color: '#8888a8', marginBottom: 14, lineHeight: 1.6 }}>
          Log withdrawals to keep your balance accurate. Withdrawals reduce your displayed balance but do NOT affect win rate, P&L or any other stats.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr auto', gap: 10, marginBottom: 10, alignItems: 'end' }}>
          <div>
            <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Amount ($)</div>
            <input className="form-input" type="number" placeholder="e.g. 500" value={wAmount} onChange={e => setWAmount(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Date</div>
            <input className="form-input" type="date" value={wDate} onChange={e => setWDate(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Note</div>
            <input className="form-input" type="text" placeholder="Optional note" value={wNote} onChange={e => setWNote(e.target.value)} />
          </div>
          <button onClick={addWithdrawal} style={{
            background: '#4cc9f0', color: '#0a0a0f', border: 'none',
            borderRadius: 8, padding: '10px 16px', fontSize: 13,
            fontWeight: 600, cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif",
            whiteSpace: 'nowrap'
          }}>+ Add</button>
        </div>
        {wSaved && <div style={{ fontSize: 12, color: '#00e5a0', marginBottom: 8 }}>✓ Withdrawal logged</div>}
        {(settings.withdrawals || []).length > 0 && (
          <div style={{ marginTop: 10 }}>
            {(settings.withdrawals || []).map(w => (
              <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #1a1a26' }}>
                <span style={{ fontSize: 12, color: '#55556a', fontFamily: "'DM Mono', monospace" }}>{w.date}</span>
                <span style={{ fontSize: 13, color: '#4cc9f0', fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>-${w.amount}</span>
                {w.note && <span style={{ fontSize: 12, color: '#8888a8' }}>{w.note}</span>}
                <button onClick={() => removeWithdrawal(w.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#55556a', padding: 0 }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <div style={{ fontSize: 12, color: '#8888a8', marginTop: 10, fontFamily: "'DM Mono', monospace" }}>
              Total withdrawn: <span style={{ color: '#4cc9f0' }}>${totalWithdrawals.toFixed(2)}</span>
            </div>
          </div>
        )}
      </Section>

      {/* Data */}
      <Section title="Public Profile & Sharing" icon={Download} color="#00e5a0">
        <PublicProfileSection shareableLink={shareableLink} />
      </Section>

      <Section title="Data Management" icon={Download}>
        <Row label="Export Trades to CSV" sub={`${trades.length} trades`}>
          <button className="btn-ghost" onClick={exportData} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={13} /> Export
          </button>
        </Row>
        <Row label="Clear All Trades" sub="Permanently deletes everything. Cannot be undone.">
          <button onClick={onClear} style={{
            background: 'rgba(255,77,109,0.1)', border: '1px solid rgba(255,77,109,0.3)',
            color: '#ff4d6d', padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: "'IBM Plex Sans', sans-serif"
          }}>
            <Trash2 size={13} /> Clear All
          </button>
        </Row>
      </Section>

      <div style={{ fontSize: 11, color: '#2a2a3e', textAlign: 'center', marginTop: 24, fontFamily: "'DM Mono', monospace" }}>
        © 2026 ODFTrades · All rights reserved
      </div>
    </div>
  );
}
